"use client";

import {
  AuthErrorBanner,
  AuthInfoBanner,
  AuthSubmitButton,
  AuthTextInput,
} from "@/app/(public)/login/_components/auth-form-primitives";
import { AuthPageShell } from "@/app/(public)/login/_components/auth-page-shell";
import { portalAuthAccent } from "@/app/(public)/login/_components/portal-auth-accent";
import type { PortalLoginBranding } from "@/app/(public)/login/_components/portal-login-screen";
import type { Translate } from "@/lib/i18n/translate";
import { publicAuthClient } from "@/lib/public-auth-client";
import { useTranslations } from "next-intl";
import { type FormEvent, type ReactElement, useState } from "react";

function genericResetInfo(t: Translate) {
  return t("kontoExistiertWurdeE");
}

export function ForgotPasswordForm({
  portalHost = false,
  portalBranding = null,
}: {
  readonly portalHost?: boolean;
  readonly portalBranding?: PortalLoginBranding | null;
}): ReactElement {
  const t = useTranslations("public.forgotPasswordForm");
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(t: Translate): Promise<void> {
    if (!email || isSubmitting) return;
    setErrorMessage(null);
    setInfoMessage(null);
    setIsSubmitting(true);
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const result = await publicAuthClient.requestPasswordReset({
        email,
        redirectTo,
      });
      if (result.error) {
        const rawMessage = result.error.message ?? "";
        if (rawMessage === "Too many requests") {
          setErrorMessage(t("vieleAnfragenBitteVersuche"));
          return;
        }
        // Everything else stays neutral on purpose: on a PUBLIC page the
        // distinction between "no such account" and "the mail failed" would
        // tell a stranger whether an address is registered. The failure is not
        // lost though — it is recorded in the delivery log for the org.
        setInfoMessage(genericResetInfo(t));
        return;
      }
      setInfoMessage(genericResetInfo(t));
    } catch (error) {
      // A thrown request never reached the backend at all (offline, 5xx). That
      // says nothing about the address, so claiming "mail sent" here was pure
      // fiction — the user would wait for something that was never requested.
      console.error(t("passwortResetAnfrageFehlgeschlagen"), error);
      setErrorMessage(t("anfrageNichtMoeglichBitteSpaeter"));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await handleSubmit(t);
  }

  return (
    <AuthPageShell
      title={t("passwortVergessen")}
      description={t("gibEMailAdresse")}
      portalHost={portalHost}
      portalBranding={portalBranding}
    >
      <form
        onSubmit={onSubmit}
        style={{
          marginTop: 28,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <AuthTextInput
          id="forgot-email"
          label={t("eMailAdresse")}
          type="email"
          value={email}
          onChange={setEmail}
          placeholder={portalHost ? t("nameBeispielDe") : t("nameVerwaltungDe")}
          autoComplete="email"
          disabled={isSubmitting}
          icon="mail"
        />

        {errorMessage ? (
          <AuthErrorBanner>{errorMessage}</AuthErrorBanner>
        ) : null}
        {infoMessage ? <AuthInfoBanner>{infoMessage}</AuthInfoBanner> : null}

        <AuthSubmitButton
          disabled={!email}
          loading={isSubmitting}
          accent={
            portalHost
              ? portalAuthAccent(portalBranding?.brand_color ?? null)
              : null
          }
        >
          {isSubmitting ? t("gesendet") : t("resetLinkSenden")}
        </AuthSubmitButton>
      </form>
    </AuthPageShell>
  );
}
